
import { FormGroup, Validators } from '@angular/forms';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class Domains {

  static getStringValidators() {
    return [Validators.maxLength(100)];
  }

  static getEmailNotRequiredValidators() {
    return [Validators.email,Validators.maxLength(50)];
  }

  static getEmailValidators() {
    return [Validators.required, Validators.email,Validators.maxLength(254)];
  }

  static getRequiredAndMaxLength5Validators() {
    return [Validators.required, Validators.maxLength(5)];
  }

  static getRequiredAndMaxLength10Validators() {
    return [Validators.required, Validators.maxLength(10)];
  }

  static getRequiredAndMaxLength50Validators() {
    return [Validators.required, Validators.maxLength(50)];
  }

  static getRequiredAlphabeticNameValidators() {
    return [Validators.required, Validators.maxLength(50), alphabeticNameValidator()];
  }

  static getRequiredAndMaxLength500Validators() {
    return [Validators.required, Validators.maxLength(500)];
  }

  static getDescriptionValidators() {
    return [Validators.maxLength(100)];
  }

  static getMaxLength50Validators() {
    return [Validators.maxLength(500)];
  }
  static getNumericValidators() {
    return [positiveNumberValidator(), Validators.maxLength(50)];
  }

  static getMaxLength100Validators() {
    return [Validators.maxLength(100)];
  }

  static getMaxLength500Validators() {
    return [Validators.maxLength(500)];
  }

  static getJsonDescriptionValidators() {
    return [Validators.maxLength(250)];
  }

  static getJsonDescriptionMaxLengthValidators() {
    return [Validators.maxLength(50)];
  }

  static getBooleanValidators() {
    return [];
  }

  static getManualCreatedValidators() {
    return [];
  }
  static getStringValidatorsMaxLength10() {
    return [Validators.maxLength(10)];
  }

  static getStringValidatorsMaxLength254() {
    return [Validators.maxLength(254)];
  }

  static getStringValidatorsMaxLength150() {
    return [Validators.maxLength(150)];
  }
  static getRequiredStringValidatorsMaxLength150() {
    return [Validators.required, Validators.maxLength(500)];
  }
  static getRequiredValidators() {
    return [Validators.required];
  }

  /** Validators for ML (multi-language) record: at least one language must have non-empty value, each value max 50 chars. */
  static getMlRequiredAndMaxLength50Validators() {
    return [mlRecordRequiredValidator(), mlRecordMaxLength50Validator()];
  }

  /** Validators for required ML record: at least one language must have a value, each value max 150 chars. */
  static getMlRequiredAndMaxLength150Validators() {
    return [mlRecordRequiredValidator(), mlRecordMaxLength150Validator()];
  }

  /** Validators for optional ML (multi-language) record: each language value max 150 chars. */
  static getMlMaxLength150Validators() {
    return [mlRecordMaxLength150Validator()];
  }

  /** Validators for optional ML (multi-language) record: each language value max 254 chars. */
  static getMlMaxLength254Validators() {
    return [mlRecordMaxLength254Validator()];
  }

  static getMultipleEmailsWithUsername(){
    return [multipleEmailsWithUsernameValidator()]
  }

  // Get URL validators array (for use in form controls)
  static getUrlValidators() {
    return [urlValidator()];
  }

  // Get password strength validators array (for use in form controls)
  static getPasswordStrengthValidators() {
    return [passwordStrengthValidator()];
  }

  /** Cognito password rules: min 12 chars, mixed case, digit, special, no whitespace. */
  static getPasswordValidators() {
    return [
      Validators.required,
      Validators.minLength(12),
      noWhitespaceValidator(),
      ...this.getPasswordStrengthValidators(),
    ];
  }

  /**
   * FormGroup-level validator for technical-support when opened from invoice:
   * if unpaid invoice rows exist, at least one must be selected (counts come from `getState`, not form controls).
   */
  static technicalSupportInvoiceSelectionValidator(
    getState: () => {
      fromInvoice: boolean;
      invoiceRowCount: number;
      selectedCount: number;
    }
  ): ValidatorFn {
    return (_control: AbstractControl): ValidationErrors | null => {
      const { fromInvoice, invoiceRowCount, selectedCount } = getState();
      if (!fromInvoice || invoiceRowCount === 0) {
        return null;
      }
      return selectedCount > 0 ? null : { invoicesRequired: true };
    };
  }

  /**
   * FormGroup-level validator for technical-support when opened from basket:
   * if the open basket has lines, at least one must be selected (counts from `getState`, not form controls).
   */
  static technicalSupportBasketSelectionValidator(
    getState: () => {
      fromBasket: boolean;
      basketItemCount: number;
      selectedCount: number;
    }
  ): ValidatorFn {
    return (_control: AbstractControl): ValidationErrors | null => {
      const { fromBasket, basketItemCount, selectedCount } = getState();
      if (!fromBasket || basketItemCount === 0) {
        return null;
      }
      return selectedCount > 0 ? null : { basketItemsRequired: true };
    };
  }

  /** Email for technical-support: required, Angular `email`, stricter host/TLD pattern, max length 254. */
  static getTechnicalSupportEmailValidators() {
    return [Validators.required, Validators.email, strictEmailFormatValidator(), Validators.maxLength(254)];
  }
}

/** Letters only (A–Z, a–z, and common accented Latin characters). No digits or special characters. */
export const ALPHABETIC_NAME_PATTERN = /^[A-Za-zÀ-ÖØ-öø-ÿ]+$/;
export const ALPHABETIC_NAME_FILTER = /[^A-Za-zÀ-ÖØ-öø-ÿ]/g;

export function alphabeticNameValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return null;
    }
    return ALPHABETIC_NAME_PATTERN.test(String(raw)) ? null : { alphabeticOnly: true };
  };
}

export function mlRecordRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || typeof value !== 'object') return { required: true };
    const hasValue = Object.values(value).some(v => v != null && String(v).trim() !== '');
    return hasValue ? null : { required: true };
  };
}

export function mlRecordMaxLength50Validator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || typeof value !== 'object') return null;
    for (const v of Object.values(value)) {
      if (v != null && String(v).length > 50) return { maxlength: { requiredLength: 50, actualLength: String(v).length } };
    }
    return null;
  };
}

/** Validator for ML (multi-language) record: each language value max 150 chars. Use for optional ML fields. */
export function mlRecordMaxLength150Validator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || typeof value !== 'object') return null;
    for (const v of Object.values(value)) {
      if (v != null && String(v).length > 150) return { maxlength: { requiredLength: 150, actualLength: String(v).length } };
    }
    return null;
  };
}

/** Validator for ML (multi-language) record: each language value max 254 chars. Use for optional ML fields. */
export function mlRecordMaxLength254Validator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || typeof value !== 'object') return null;
    for (const v of Object.values(value)) {
      if (v != null && String(v).length > 254) return { maxlength: { requiredLength: 254, actualLength: String(v).length } };
    }
    return null;
  };
}

/**
 * Stricter than `Validators.email`: requires a domain label, dot, and TLD of at least two letters (after trim).
 */
export function strictEmailFormatValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return null;
    }
    const value = String(raw).trim();
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(value) ? null : { emailStrict: true };
  };
}

export function positiveNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    // Allow empty/null values (handled by required validator if needed)
    if (value === null || value === undefined || value === '') {
      return null;
    }
    // Convert to number and check if it's a positive number (> 0)
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < 0) {
      return { positiveNumber: { value: value } };
    }
    return null;
  };
}



export function MustMatch(controlName: string, matchingControlName: string) {
  return (formGroup: FormGroup): void => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    if (!control || !matchingControl) {
      return;
    }

    if (matchingControl.errors && !matchingControl.errors['mustMatch']) {
      return;
    }

    if (control.value !== matchingControl.value) {
      // Add mustMatch error while preserving existing errors
      const existingErrors = matchingControl.errors || {};
      matchingControl.setErrors({ ...existingErrors, mustMatch: true });
    } else {
      // Remove mustMatch error but preserve other errors
      if (matchingControl.errors) {
        const { mustMatch, ...otherErrors } = matchingControl.errors;
        const hasOtherErrors = Object.keys(otherErrors).length > 0;
        matchingControl.setErrors(hasOtherErrors ? otherErrors : null);
      }
    }
  };
}


// password and confirm password match
 export function MustDifferent(controlName: string, matchingControlName: string) {
   return (formGroup: FormGroup) => {
     const control = formGroup.controls[controlName];
     const matchingControl = formGroup.controls[matchingControlName];
     
     if (!control || !matchingControl) {
       return;
     }
    
    if (control.value === matchingControl.value && control.value) {
      // Add mustDifferent error while preserving existing errors
      const existingErrors = matchingControl.errors || {};
      matchingControl.setErrors({ ...existingErrors, mustDifferent: true });
    } else {
      // Remove mustDifferent error but preserve other errors
      if (matchingControl.errors) {
        const { mustDifferent, ...otherErrors } = matchingControl.errors;
        const hasOtherErrors = Object.keys(otherErrors).length > 0;
        matchingControl.setErrors(hasOtherErrors ? otherErrors : null);
      }
     }
   };
 }

 export function multipleEmailsWithUsernameValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null; // If the field is empty, no error is returned
    }

    // Split the input value by comma or semicolon to get individual emails
    const emails = value.split(/[\s,;]+/).map(email => email.trim());

    // Email regex pattern
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Loop through each email to check if it's valid
    for (const email of emails) {
      // If the email starts with '@', assume it's a username and skip the regex validation
      if (!email.startsWith('@') && !emailPattern.test(email)) {
        // Return the invalid email error if one of them fails the validation
        return { invalidEmail: true };
      }
    }

    // If all emails are valid, return null (no error)
    return null;
  };
}

// URL validator - allows empty values (optional field)
export function urlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || control.value.trim() === '') {
      return null; // Allow empty values (field is optional)
    }
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*(\?[^\s]*)?(#[^\s]*)?$/i;
    const isValid = urlPattern.test(control.value);
    return isValid ? null : { invalidUrl: true };
  };
}

// Password strength validator - requires at least one uppercase, one lowercase, one special character, and one digit
export function passwordStrengthValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || control.value.trim() === '') {
      return null; // Let required validator handle empty values
    }

    const value = control.value;
    const errors: ValidationErrors = {};

    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(value)) {
      errors['passwordUppercase'] = true;
    }

    // Check for at least one lowercase letter
    if (!/[a-z]/.test(value)) {
      errors['passwordLowercase'] = true;
    }

    // At least one non-alphanumeric character
    if (!/[^A-Za-z0-9]/.test(value)) {
      errors['passwordSpecial'] = true;
    }

    // Check for at least one digit
    if (!/[0-9]/.test(value)) {
      errors['passwordDigit'] = true;
    }

    // Return null if no errors, otherwise return the errors object
    return Object.keys(errors).length > 0 ? errors : null;
  };
}

export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    return /\s/.test(control.value) ? { passwordWhitespace: true } : null;
  };
}

/** Rejects a password made of one character repeated, such as aaaaaaaaaaaa. */
export function passwordNotRepeatedCharacterValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || value === '') {
      return null;
    }
    return /^(.)\1+$/.test(String(value)) ? { passwordRepeated: true } : null;
  };
}

/**
 * Rejects a password that contains the email local part when that part is 3 characters or longer.
 */
export function passwordExcludesEmailLocalPartValidator(
  getEmail: () => string | null | undefined
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const password = control.value;
    if (password == null || password === '') {
      return null;
    }
    const email = (getEmail() ?? '').trim().toLowerCase();
    const at = email.indexOf('@');
    if (at < 3) {
      return null;
    }
    const localPart = email.slice(0, at);
    return String(password).toLowerCase().includes(localPart)
      ? { passwordEmailLocalPart: true }
      : null;
  };
}
